import api from '@/lib/axios';

export type EmploymentType = 'none' | 'clt' | 'pj' | 'socio' | 'estagio';

export type EmployeeDocumentType =
  | 'employment_contract'
  | 'addendum'
  | 'nfs'
  | 'payment_receipt'
  | 'medical_certificate'
  | 'confidentiality_agreement';

export interface EmployeeListItem {
  id: string;
  email: string;
  employmentType: EmploymentType;
  active: boolean;
  createdAt: string;
  fullName?: string | null;
  phone?: string | null;
  birthDate?: string | null;
  cnpj?: string | null;
  avatarUrl?: string | null;
  teams: EmployeeTeam[];
  contractEntryDate?: string | null;
  contractExitDate?: string | null;
  hasContract: boolean;
}

export interface EmployeeTeam {
  id: string;
  name: string;
}

export type EmployeeListableType = Exclude<EmploymentType, 'none'>;

export interface ListEmployeesFilters {
  active?: boolean;
  search?: string;
  employmentType?: EmployeeListableType;
  sortBy?: 'name' | 'hasCnpj';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface PaginatedEmployees {
  items: EmployeeListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Item leve retornado por `GET /employees?forSelect=true`. */
export interface EmployeeSelectItem {
  id: string;
  email: string;
  fullName?: string | null;
}

export interface EmployeeProfessionalData {
  fullName: string;
  address: string;
  phone: string;
  rg: string;
  cpf: string;
  email: string;
  birthDate?: string | null;
  cnpj?: string | null;
  companyAddress?: string | null;
  companyEmail?: string | null;
  pixKey?: string | null;
  payingCompanyId?: string | null;
  paymentDate?: string | null;
  proLabore?: string | null;
}

export interface EmployeeDetails {
  id: string;
  email: string;
  employmentType: EmploymentType;
  active: boolean;
  createdAt: string;
  professionalData?: EmployeeProfessionalData | null;
  avatarUrl?: string | null;
  teams?: EmployeeTeam[];
  contractEntryDate?: string | null;
  contractExitDate?: string | null;
  hasContract?: boolean;
}

export interface EmployeeContract {
  id: string;
  tenantId: string;
  userId: string;
  contractType: 'contract' | 'addendum';
  parentContractId?: string | null;
  startDate: string;
  endDate: string;
  value?: string | null;
  scope?: string | null;
  filePath: string;
  storageKey: string;
  documentType: EmployeeDocumentType;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContractWithUser {
  contract: EmployeeContract;
  user: {
    id: string;
    email: string;
  };
  professionalData: {
    fullName: string | null;
  } | null;
}

export interface EmployeeAuditLog {
  id: string;
  entityId: string;
  action: 'create' | 'update' | 'delete';
  changedFields?: Record<string, { old: any; new: any }>;
  createdAt: string;
  changedBy?: string | null;
  changedByEmail?: string | null;
  employeeEmail?: string | null;
  employeeFullName?: string | null;
}

export interface ContractAuditLog {
  id: string;
  entityId: string;
  action: 'create' | 'update' | 'delete';
  changedFields?: Record<string, { old: any; new: any }>;
  createdAt: string;
  changedBy?: string | null;
  changedByEmail?: string | null;
  contractUserId?: string | null;
  contractStartDate?: string | null;
  contractEndDate?: string | null;
  employeeFullName?: string | null;
}

export interface ContractSettings {
  notifyTeamId: string | null;
  daysBeforeExpiration: number;
  notifyByEmail: boolean;
}

export interface CreateContractDto {
  contractType?: 'contract' | 'addendum';
  parentContractId?: string;
  startDate: string;
  endDate: string;
  value?: number;
  scope?: string;
  documentType: EmployeeDocumentType;
}

export interface UpdateContractDto {
  contractType?: 'contract' | 'addendum';
  parentContractId?: string | null;
  startDate?: string;
  endDate?: string;
  value?: number;
  scope?: string;
  documentType?: EmployeeDocumentType;
}

export interface UpdateEmployeeDto {
  employmentType?: EmploymentType;
  fullName?: string;
  address?: string;
  phone?: string;
  rg?: string;
  cpf?: string;
  email?: string;
  birthDate?: string;
  cnpj?: string;
  companyAddress?: string;
  companyEmail?: string;
  pixKey?: string | null;
  payingCompanyId?: string | null;
  paymentDate?: string | null;
  proLabore?: number;
}

export interface Recess {
  id: string;
  tenantId: string;
  userId: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Vacation {
  id: string;
  tenantId: string;
  userId: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecessSummary {
  accumulatedDays: number;
  usedDays: number;
  pendingDays: number;
  workDays: number;
  lastRecessDate: string | null;
  daysSinceLastRecess: number;
  alerts: string[];
}

export interface VacationSummary {
  accumulatedDays: number;
  usedDays: number;
  pendingDays: number;
  workDays: number;
  lastVacationDate: string | null;
  daysSinceLastVacation: number;
  alerts: string[];
}

export interface CreateRecessDto {
  startDate: string;
  endDate: string;
}

export interface UpdateRecessDto {
  startDate?: string;
  endDate?: string;
}

export interface CreateVacationDto {
  startDate: string;
  endDate: string;
}

export interface UpdateVacationDto {
  startDate?: string;
  endDate?: string;
}

export interface RecessControlEmployee {
  id: string;
  email: string;
  employmentType: EmploymentType;
  fullName?: string | null;
  type: 'PJ' | 'CLT';
  accumulatedDays: number;
  usedDays: number;
  pendingDays: number;
}

export interface CalendarEvent {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  totalDays: number;
  fullName: string | null;
  email: string;
  type: 'recess' | 'vacation';
}

export const employeesService = {
  async listEmployees(filters: ListEmployeesFilters = {}): Promise<PaginatedEmployees> {
    const params: Record<string, string | number> = {};
    if (filters.active !== undefined) params.active = String(filters.active);
    const search = filters.search?.trim();
    if (search) params.search = search;
    if (filters.employmentType) params.employmentType = filters.employmentType;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
    const response = await api.get<PaginatedEmployees>('/employees', { params });
    return response.data;
  },

  /** Lista plana (sem paginação) para seletores de prestador. */
  async listEmployeesForSelect(filters?: {
    active?: boolean;
    employmentType?: EmployeeListableType;
  }): Promise<EmployeeSelectItem[]> {
    const params: Record<string, string> = { forSelect: 'true' };
    if (filters?.active !== undefined) params.active = String(filters.active);
    if (filters?.employmentType) params.employmentType = filters.employmentType;
    const response = await api.get<EmployeeSelectItem[]>('/employees', { params });
    return response.data;
  },

  async getEmployee(id: string): Promise<EmployeeDetails> {
    const response = await api.get<EmployeeDetails>(`/employees/${id}`);
    return response.data;
  },

  async updateEmployee(id: string, data: UpdateEmployeeDto): Promise<EmployeeDetails> {
    const response = await api.patch<EmployeeDetails>(`/employees/${id}`, data);
    return response.data;
  },

  async listEmployeeContracts(userId: string): Promise<EmployeeContract[]> {
    const response = await api.get<EmployeeContract[]>(`/employees/${userId}/contracts`);
    return response.data;
  },

  async createContract(
    userId: string,
    data: CreateContractDto,
    file: File,
  ): Promise<EmployeeContract> {
    const formData = new FormData();
    formData.append('startDate', data.startDate);
    formData.append('endDate', data.endDate);
    formData.append('documentType', data.documentType);
    if (data.contractType) formData.append('contractType', data.contractType);
    if (data.parentContractId) formData.append('parentContractId', data.parentContractId);
    if (data.value !== undefined) formData.append('value', data.value.toString());
    if (data.scope) formData.append('scope', data.scope);
    formData.append('file', file);

    const response = await api.post<EmployeeContract>(
      `/employees/${userId}/contracts`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  async updateContract(id: string, data: UpdateContractDto): Promise<EmployeeContract> {
    const response = await api.patch<EmployeeContract>(`/contracts/${id}`, data);
    return response.data;
  },

  async deleteContract(id: string): Promise<void> {
    await api.delete(`/contracts/${id}`);
  },

  async getContractDownloadUrl(id: string): Promise<{ url: string }> {
    const response = await api.get<{ url: string }>(`/contracts/${id}/download`);
    return response.data;
  },

  async listContracts(filters?: { userId?: string; startDate?: string; endDate?: string }) {
    const response = await api.get<ContractWithUser[]>('/contracts', { params: filters });
    return response.data;
  },

  async getEmployeeLogs(): Promise<EmployeeAuditLog[]> {
    const response = await api.get<EmployeeAuditLog[]>('/employee-logs');
    return response.data;
  },

  async getContractLogs(): Promise<ContractAuditLog[]> {
    const response = await api.get<ContractAuditLog[]>('/contract-logs');
    return response.data;
  },

  async getContractSettings(): Promise<ContractSettings> {
    const response = await api.get<ContractSettings>('/contracts/settings');
    return response.data;
  },

  async updateContractSettings(settings: Partial<ContractSettings>): Promise<ContractSettings> {
    const response = await api.patch<ContractSettings>('/contracts/settings/edit', settings);
    return response.data;
  },

  // ========== RECESSO ==========

  async getRecessSummary(userId: string): Promise<RecessSummary> {
    const response = await api.get<RecessSummary>(`/employees/${userId}/recess-summary`);
    return response.data;
  },

  async listEmployeeRecesses(userId: string): Promise<Recess[]> {
    const response = await api.get<Recess[]>(`/employees/${userId}/recesses`);
    return response.data;
  },

  async createRecess(userId: string, data: CreateRecessDto): Promise<Recess> {
    const response = await api.post<Recess>(`/employees/${userId}/recesses`, data);
    return response.data;
  },

  async updateRecess(id: string, data: UpdateRecessDto): Promise<Recess> {
    const response = await api.patch<Recess>(`/recesses/${id}`, data);
    return response.data;
  },

  async deleteRecess(id: string): Promise<void> {
    await api.delete(`/recesses/${id}`);
  },

  // ========== FÉRIAS ==========

  async getVacationSummary(userId: string): Promise<VacationSummary> {
    const response = await api.get<VacationSummary>(`/employees/${userId}/vacation-summary`);
    return response.data;
  },

  async listEmployeeVacations(userId: string): Promise<Vacation[]> {
    const response = await api.get<Vacation[]>(`/employees/${userId}/vacations`);
    return response.data;
  },

  async createVacation(userId: string, data: CreateVacationDto): Promise<Vacation> {
    const response = await api.post<Vacation>(`/employees/${userId}/vacations`, data);
    return response.data;
  },

  async updateVacation(id: string, data: UpdateVacationDto): Promise<Vacation> {
    const response = await api.patch<Vacation>(`/vacations/${id}`, data);
    return response.data;
  },

  async deleteVacation(id: string): Promise<void> {
    await api.delete(`/vacations/${id}`);
  },

  // ========== CONTROLE ADMINISTRATIVO ==========

  async getRecessControlList(): Promise<RecessControlEmployee[]> {
    const response = await api.get<RecessControlEmployee[]>('/recess-control');
    return response.data;
  },

  async getRecessCalendar(startDate?: string, endDate?: string): Promise<{
    recesses: CalendarEvent[];
    vacations: CalendarEvent[];
  }> {
    const response = await api.get<{ recesses: CalendarEvent[]; vacations: CalendarEvent[] }>(
      '/recess-control/calendar',
      { params: { startDate, endDate } },
    );
    return response.data;
  },
};
